/**
 * CoverCarousel — the `feature` card's cover band (ELARA-1055).
 *
 * `DetailCardLayout` used to render only `images[0]` as a static cover, so a room with fifteen photos
 * and a room with one looked identical. This pages through the set one image at a time. It serves the
 * carousel card, where the band is the card's whole width; the single `lg` card shows a scrollable
 * strip of photos instead, which a card that narrow has no room for. The RN analog is
 * `mobile/src/ui/cards/CoverCarousel.tsx`, a swipeable band with the same page dots.
 *
 * The band fills whatever box the caller gives it: `className` sets that box, and every layer below is
 * `h-full`. The `aspect-video` default is only there so a caller that passes no size still gets a
 * sensible band rather than a collapsed one.
 *
 * Four deliberate choices:
 *
 * - **Clicking an image opens the card, not a lightbox.** It used to open the lightbox, which made the
 *   photo the one part of a card that did something different from the rest of it — and on a card in a
 *   carousel that is a trap, since the photo is most of the card's surface. The whole card now goes one
 *   place: the guide, where the full gallery lives anyway. The arrows are siblings of the image links
 *   rather than inside them, so paging never navigates.
 * - **It is a paged track, not a scroll container — and that is the whole point.** These cards sit in a
 *   horizontally-scrolling row, and any inner scroller placed on top of a photo steals the gesture
 *   meant for that row: touch swipe and trackpad pan alike go to the innermost scrollable box, so the
 *   row would not move while the pointer was over an image — which is most of a card. Two narrower
 *   fixes were tried and both failed. `overflow-x: hidden` stops the user but stopped the arrows too;
 *   `touch-action: pan-y` declines touch pans but says nothing about a trackpad. Having no scroller at
 *   all is what actually settles it: the nearest scrollable ancestor is the row of cards, so every
 *   horizontal gesture over a photo reaches it. Paging is by arrow, and the dots say how many photos
 *   there are — the thing a swipe was really advertising.
 * - **The arrows are frosted and appear on hover.** They sit on a photograph, where a solid chip read
 *   as a floating button competing with the image. The translucent surface matches the shortlist pill
 *   in the corner above, so the two controls on one photo read as the same kind of control. They stay
 *   visible on a device with no hover, where there is no gesture left to page the band with.
 * - **Paging is capped.** At most `MAX_CARD_COVER_IMAGES` page in the band — a card in a chat message
 *   is a preview, not a gallery, and the rest are in the guide the card opens. Mobile caps the same way.
 * - **The images are absolutely positioned, and that is load-bearing.** The `lg` cover column has no
 *   definite height — it takes one from the content column via `self-stretch`. An in-flow `<img>` in
 *   that column contributes its INTRINSIC height to the flex line, so a 2000px photo dragged the whole
 *   card to 2000px once it loaded (and the card looked fine until then, which is what made it
 *   puzzling). Taking the image out of flow leaves the band contributing only its `min-h` floor, so the
 *   card is as tall as its text and the photo crops to fit.
 * - **It downloads what it draws, and only what it can reach.** Each photo is fetched at the band's
 *   measured width (`AppImage`'s `displayWidth`: Travelport's Leonardo originals are 6016px and ~8 MB,
 *   and a Rooms grid of them locked the guide up on scroll), with the original as the fallback. And only
 *   the pages paged to so far, plus the next, load: the browser's lazy loading counts the hidden pages
 *   beside the visible one as near the viewport, so it fetched all five per card.
 * - **The band paints its own surface.** It is the placeholder: a tinted panel plus the entity's
 *   fallback icon, visible until a photo paints over it. `background-secondary` is the page's own
 *   colour, so a band using it vanished into the page while images loaded — hence `quaternary`, which
 *   is distinct from both the white card and the page in light and dark themes.
 */
import type { WidgetImage } from '@elara/domain/models/schemas/widget-schemas';

import { useState } from 'react';

import { cn } from '@elara/client-core/lib/utils';

import { getFallbackCoverImageIcon, IconSizes, MaterialIcon } from '#ui';
import { AppImage } from '#ui/AppImage.js';
import { useResizeObserver } from '#ui/hooks/useResizeObserver.js';

/** How many images page in the card band. The rest are in the guide the card opens. */
export const MAX_CARD_COVER_IMAGES = 5;

export interface CoverCarouselProps {
  images: WidgetImage[];
  /** The card's title — the alt-text fallback for images carrying neither `alt` nor `caption`. */
  title?: string;
  /** Entity type, for the placeholder icon shown until a photo paints. */
  type?: string;
  className?: string;
  /** The card's destination. Given either of these, a photo click goes where the rest of the card goes. */
  href?: string;
  onClick?: React.MouseEventHandler;
}

export function CoverCarousel({ images, title, type, className, href, onClick }: CoverCarouselProps) {
  const [index, setIndex] = useState(0);
  // Ids of images whose URL did not resolve. Suppliers' photo CDNs rot — a third of the URLs on some
  // stored rooms 404 — and a broken `<img>` is not a neutral thing: the browser draws its own broken
  // glyph or the alt text over the band, which looks like a rendering fault rather than a missing
  // photo. Dropping them from the set entirely is the better fallback: the band falls back to its own
  // placeholder, and — because the dots and arrows count only what is left — nobody pages onto a blank
  // frame that will never fill.
  const [failedIds, setFailedIds] = useState<ReadonlySet<string>>(() => new Set());
  // The furthest page reached. Pages up to the one after it load; the rest wait until paged towards.
  const [reachedIndex, setReachedIndex] = useState(0);
  // Photos wait for the band's width, so each is fetched once at the size it is drawn rather than at a
  // guess and then again.
  const [bandRef, { width: bandWidth }] = useResizeObserver<HTMLDivElement>();

  const pagedImages = images.slice(0, MAX_CARD_COVER_IMAGES).filter((image) => !failedIds.has(image.id));
  const lastIndex = pagedImages.length - 1;
  // An image failing shortens the set under the current position, so clamp rather than trusting state.
  const activeIndex = Math.min(index, Math.max(lastIndex, 0));
  // Clamped rather than wrapped: a gallery that loops gives no sense of where its end is, and these
  // are at most five photos.
  const page = (delta: number) => {
    const next = Math.min(Math.max(activeIndex + delta, 0), lastIndex);
    setIndex(next);
    setReachedIndex((reached) => Math.max(reached, next));
  };

  const arrow = (direction: 'left' | 'right') => {
    const enabled = direction === 'left' ? activeIndex > 0 : activeIndex < lastIndex;
    if (!enabled) return;
    return (
      <button
        type="button"
        onClick={() => page(direction === 'left' ? -1 : 1)}
        aria-label={direction === 'left' ? 'Previous image' : 'Next image'}
        className={cn(
          'absolute top-1/2 z-10 flex size-7 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full',
          'bg-background/70 text-foreground shadow-sm backdrop-blur-md transition hover:bg-background/90',
          direction === 'left' ? 'left-2' : 'right-2',
          // Hidden until the pointer is on the photo, so the picture is clean at rest. Tailwind's
          // `hover:` is already `@media (hover: hover)`, so the escape hatch below is what keeps the
          // arrows usable on a touch device — where, with no swipe, they are the only way to page.
          'opacity-0 group-hover/cover:opacity-100 focus-visible:opacity-100 [@media(hover:none)]:opacity-100',
        )}
      >
        <MaterialIcon icon={direction === 'left' ? 'chevron_left' : 'chevron_right'} size={IconSizes.sm} />
      </button>
    );
  };

  return (
    <div
      ref={bandRef}
      className={cn('group/cover relative w-full aspect-video overflow-hidden bg-background-quaternary', className)}
    >
      {/* The placeholder sits beneath the band and simply stays there — the images paint over it, so
          there is no empty-to-loaded swap to time, and a photo that never loads degrades to this. */}
      <div className="absolute inset-0 flex items-center justify-center" aria-hidden="true">
        <MaterialIcon
          icon={getFallbackCoverImageIcon(type ?? '')}
          size={IconSizes.md}
          className="text-muted-foreground/40"
          weight="light"
        />
      </div>

      {/* The track holds every page side by side and slides as a whole. `translateX` is a compositor
          property, so paging does not lay anything out again. */}
      <div
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {pagedImages.map((image, slideIndex) => {
          const isReachable = slideIndex <= Math.max(reachedIndex, activeIndex) + 1;
          const photo = bandWidth > 0 && isReachable && (
            <AppImage
              src={image.url}
              displayWidth={bandWidth}
              alt={image.alt ?? image.caption ?? title ?? ''}
              // Dropped only when the original fails too: a missing rendition falls back to it.
              onAllFailed={() => setFailedIds((current) => new Set(current).add(image.id))}
              className="absolute inset-0 h-full w-full object-cover"
            />
          );
          const slideClasses = 'relative block h-full w-full shrink-0 overflow-hidden';
          const label = image.caption ?? image.alt ?? `View ${title ?? 'details'}`;

          // An anchor only when there is somewhere to go. `href`-less anchors are not focusable and
          // carry no role, and most callers here open a guide through a handler rather than a URL —
          // so that case gets a real button instead, and the photo stays reachable by keyboard.
          if (href) {
            return (
              <a
                key={image.id}
                href={href}
                onClick={onClick}
                aria-label={label}
                // Off-screen pages must not be tab stops, or keyboard focus walks into the hidden ones.
                tabIndex={slideIndex === activeIndex ? undefined : -1}
                className={cn(slideClasses, 'cursor-pointer')}
              >
                {photo}
              </a>
            );
          }
          if (onClick) {
            return (
              <button
                key={image.id}
                type="button"
                onClick={onClick}
                aria-label={label}
                tabIndex={slideIndex === activeIndex ? undefined : -1}
                className={cn(slideClasses, 'cursor-pointer')}
              >
                {photo}
              </button>
            );
          }
          // Nowhere to go: the photo is decorative, so it gets no control wrapping it.
          return (
            <div key={image.id} className={slideClasses}>
              {photo}
            </div>
          );
        })}
      </div>

      {arrow('left')}
      {arrow('right')}

      {/* Dots, not a counter, and only when there is more than one: they say how many photos there are
          at a glance, which is what a swipe used to imply. `pointer-events-none` so they never
          intercept a click meant for the photo beneath. The ring is what keeps them legible on a pale
          sky as well as a night shot. */}
      {pagedImages.length > 1 && (
        <div
          data-testid="cover-carousel-dots"
          className="pointer-events-none absolute inset-x-0 bottom-2 z-10 flex items-center justify-center gap-1.5"
          aria-hidden="true"
        >
          {pagedImages.map((image, dotIndex) => (
            <span
              key={image.id}
              className={cn(
                'size-1.5 rounded-full ring-1 ring-black/20 transition-colors',
                dotIndex === activeIndex ? 'bg-white' : 'bg-white/50',
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}
